import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Settings, Mail, Save, AlertCircle, CheckCircle, Users, Plus, Edit2, Trash2, X, UserPlus, Upload, Image as ImageIcon, Send, Monitor } from 'lucide-react';
import axios from 'axios';
import DashboardSettings from './DashboardSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GlobalSettings = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('users'); // Start with users tab
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // SMTP Settings State
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'QC System'
  });

  // Knack Settings State
  const [knackSettings, setKnackSettings] = useState({
    knack_app_id: '',
    knack_api_key: '',
    knack_api_url: 'https://api.knack.com/v1'
  });

  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [userListView, setUserListView] = useState('employees'); // 'employees' or 'suppliers'
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    profile_keys: [],
    department: '',
    avatar: null,
    avatarPreview: null
  });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [sendingCredentials, setSendingCredentials] = useState(false);
  const fileInputRef = useRef(null);

  const isAdmin = user?.role?.toLowerCase() === 'admin' || 
                  user?.roles?.some(r => r?.toLowerCase() === 'admin');

  // Role options for user management with colors
  const roleOptions = [
    { key: 'profile_3', label: 'Employee', description: 'Standard employee access', color: 'blue' },
    { key: 'profile_4', label: 'Viewer', description: 'View-only access', color: 'gray' },
    { key: 'profile_5', label: 'Admin', description: 'Full system access', color: 'red' },
    { key: 'profile_20', label: 'PMO', description: 'Project Management Office', color: 'indigo' },
    { key: 'profile_25', label: 'Airports Division Manager', description: 'Manages airport projects', color: 'green' },
    { key: 'profile_26', label: 'CNS Division Manager', description: 'Manages CNS projects', color: 'teal' },
    { key: 'profile_27', label: 'Business Development', description: 'Business development team', color: 'purple' },
    { key: 'profile_28', label: 'Finance', description: 'Finance department', color: 'emerald' },
    { key: 'profile_34', label: 'Supplier', description: 'External supplier/manufacturer', color: 'orange' }
  ];

  useEffect(() => {
    if (isAdmin) {
      if (activeTab === 'smtp') {
        fetchSettings();
      } else if (activeTab === 'users') {
        fetchUsers();
      } else if (activeTab === 'knack') {
        fetchKnackSettings();
      }
    }
  }, [isAdmin, activeTab]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/qc-incidents/settings/global`, { headers });
      
      setSettings({
        smtp_host: response.data.smtp_host || '',
        smtp_port: response.data.smtp_port || 587,
        smtp_username: response.data.smtp_username || '',
        smtp_password: response.data.smtp_password || '',
        smtp_from_email: response.data.smtp_from_email || '',
        smtp_from_name: response.data.smtp_from_name || 'QC System'
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const fetchKnackSettings = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/qc-incidents/settings/knack`, { headers });
      
      setKnackSettings({
        knack_app_id: response.data.knack_app_id || '',
        knack_api_key: response.data.knack_api_key || '',
        knack_api_url: response.data.knack_api_url || 'https://api.knack.com/v1'
      });
    } catch (error) {
      console.error('Failed to fetch Knack settings:', error);
      // Initialize with env values if API fails
      setKnackSettings({
        knack_app_id: 'Not configured',
        knack_api_key: 'Not configured',
        knack_api_url: 'https://api.knack.com/v1'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/users?rows_per_page=1000`, { headers });
      
      console.log('=== USERS API RESPONSE ===');
      console.log('Full response:', response.data);
      const fetchedUsers = response.data.users || response.data.records || [];
      console.log('Total users fetched:', fetchedUsers.length);
      
      // Split into employees and suppliers
      const employeesList = [];
      const suppliersList = [];
      
      fetchedUsers.forEach(user => {
        const keysArray = user.profile_keys_raw || [];
        const isSupplier = keysArray.some(k => k.id === 'profile_29' || k.id === 'profile_34');
        
        if (isSupplier) {
          suppliersList.push(user);
        } else {
          employeesList.push(user);
        }
      });
      
      setUsers(employeesList);
      setSuppliers(suppliersList);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setMessage({ type: 'error', text: 'Failed to load users' });
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!settings.smtp_host || !settings.smtp_username || !settings.smtp_password || !settings.smtp_from_email) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/qc-incidents/settings/global`, settings, { headers });
      
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveKnackSettings = async (e) => {
    e.preventDefault();
    
    if (!knackSettings.knack_app_id || !knackSettings.knack_api_key) {
      setMessage({ type: 'error', text: 'App ID and API Key are required' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/qc-incidents/settings/knack`, knackSettings, { headers });
      
      setMessage({ type: 'success', text: 'Knack settings saved successfully! Please restart the backend for changes to take effect.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Failed to save Knack settings:', error);
      setMessage({ type: 'error', text: 'Failed to save Knack settings' });
    } finally {
      setSaving(false);
    }
  };

  // User Management Functions
  const handleOpenUserModal = (user = null) => {
    if (user) {
      console.log('=== OPENING EDIT MODAL ===');
      console.log('User object:', user);
      
      setEditingUser(user);
      
      // Get avatar URL
      const avatarUrl = user.field_115_raw?.s3?.url || user.field_115_raw?.url || user.field_115;
      
      const formData = {
        name: user.field_12 || (typeof user.name === 'string' ? user.name : user.name?.full) || '',
        email: user.field_13_raw?.email || user.email || '',
        phone: user.field_16 || user.phone || '',
        password: '',
        profile_keys: Array.isArray(user.profile_keys) ? user.profile_keys : [],
        department: user.department || '',
        avatar: null,
        avatarPreview: avatarUrl || null
      };
      console.log('Form data populated:', formData);
      setUserFormData(formData);
    } else {
      console.log('Opening modal for NEW user');
      setEditingUser(null);
      setUserFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        profile_keys: ['profile_3'], // Default to Employee role
        department: '',
        avatar: null,
        avatarPreview: null
      });
    }
    setShowUserModal(true);
  };

  const handleCloseUserModal = () => {
    setShowUserModal(false);
    setEditingUser(null);
    setUserFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      profile_keys: [],
      avatar: null,
      avatarPreview: null
    });
  };

  const handleToggleRole = (profileKey) => {
    setUserFormData(prev => {
      const newProfileKeys = prev.profile_keys.includes(profileKey)
        ? prev.profile_keys.filter(k => k !== profileKey)
        : [...prev.profile_keys, profileKey];
      return { ...prev, profile_keys: newProfileKeys };
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleAvatarFile(file);
    }
  };

  const handleAvatarPaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          handleAvatarFile(file);
        }
      }
    }
  };

  const handleAvatarFile = (file) => {
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setUserFormData(prev => ({
        ...prev,
        avatar: file,
        avatarPreview: reader.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    
    if (userFormData.profile_keys.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one role' });
      return;
    }

    // Validate required fields for new users
    if (!editingUser) {
      if (!userFormData.name || !userFormData.email) {
        setMessage({ type: 'error', text: 'Name and email are required' });
        return;
      }
      if (!userFormData.password || userFormData.password.length < 6) {
        setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
        return;
      }
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      console.log('Updating user with data:', userFormData);
      
      // Upload avatar first if there's a new one
      if (userFormData.avatar) {
        setUploadingAvatar(true);
        const formData = new FormData();
        formData.append('file', userFormData.avatar);
        
        try {
          await axios.post(`${API}/users/${editingUser.id}/avatar`, formData, {
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data'
            }
          });
          console.log('Avatar uploaded successfully');
        } catch (avatarError) {
          console.error('Avatar upload failed:', avatarError);
          setMessage({ type: 'error', text: 'Failed to upload avatar' });
          setSaving(false);
          setUploadingAvatar(false);
          return;
        }
        setUploadingAvatar(false);
      }
      
      let response;
      
      if (editingUser) {
        // UPDATE existing user
        const updateData = {
          profile_keys: userFormData.profile_keys
        };
        
        // Update name if changed
        if (userFormData.name !== editingUser.field_12) {
          updateData.field_12 = userFormData.name;
          // Try to split into first/last for field_12_raw
          const nameParts = userFormData.name.trim().split(' ');
          const first = nameParts[0] || '';
          const last = nameParts.slice(1).join(' ') || '';
          updateData.field_12_raw = {
            ...(editingUser.field_12_raw || {}),
            first,
            last,
            full: userFormData.name
          };
        }
        
        // Update email if changed - preserve existing field_13_raw structure
        if (userFormData.email !== editingUser.field_13_raw?.email) {
          updateData.field_13_raw = {
            ...(editingUser.field_13_raw || {}),
            email: userFormData.email
          };
          updateData.field_13 = `<a href="mailto:${userFormData.email}">${userFormData.email}</a>`;
        }
        
        // Update phone if changed
        if (userFormData.phone !== editingUser.field_16) {
          updateData.field_16 = userFormData.phone;
        }
        
        // Update department
        if (userFormData.department !== editingUser.department) {
          updateData.department = userFormData.department;
        }
        
        // Only include password if it's not empty
        if (userFormData.password && userFormData.password.trim()) {
          updateData.password = userFormData.password;
        }
        
        console.log('Sending update data:', updateData);
        response = await axios.put(`${API}/users/${editingUser.id}`, updateData, { headers });
        console.log('Update response:', response.data);
        setMessage({ type: 'success', text: 'User updated successfully!' });
      } else {
        // CREATE new user
        const createData = {
          field_12: userFormData.name,
          field_13_raw: { email: userFormData.email },
          field_16: userFormData.phone,
          profile_keys: userFormData.profile_keys,
          department: userFormData.department,
          password: userFormData.password
        };
        
        console.log('Sending create data:', createData);
        response = await axios.post(`${API}/users`, createData, { headers });
        console.log('Create response:', response.data);
        setMessage({ type: 'success', text: 'User created successfully!' });
      }
      
      handleCloseUserModal();
      fetchUsers();
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Failed to save user:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save user';
      setMessage({ type: 'error', text: `Error: ${errorMsg}` });
    } finally {
      setSaving(false);
    }
  };

  const handleSendCredentials = async (userId) => {
    if (!window.confirm('Send login credentials to this user via email?')) {
      return;
    }

    setSendingCredentials(true);
    setMessage({ type: '', text: '' });

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/users/${userId}/send-credentials`, {}, { headers });
      
      setMessage({ type: 'success', text: 'Credentials sent successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Failed to send credentials:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send credentials';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSendingCredentials(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/users/${userId}`, { headers });
      
      setMessage({ type: 'success', text: 'User deleted successfully!' });
      fetchUsers();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Failed to delete user:', error);
      setMessage({ type: 'error', text: 'Failed to delete user' });
    }
  };

  const getRoleBadges = (profileKeys) => {
    console.log('getRoleBadges called with:', profileKeys, 'Type:', typeof profileKeys, 'IsArray:', Array.isArray(profileKeys));
    
    // Convert to array if it's a string
    let keysArray = profileKeys;
    if (typeof profileKeys === 'string') {
      // Split by comma and trim whitespace
      keysArray = profileKeys.split(',').map(k => k.trim());
      console.log('Converted string to array:', keysArray);
    }
    
    // Ensure profileKeys is an array
    if (!keysArray || !Array.isArray(keysArray) || keysArray.length === 0) {
      console.log('No valid profile keys, returning "No roles"');
      return <span className="text-gray-400 text-sm">No roles</span>;
    }
    
    console.log('Processing profile keys:', keysArray);
    
    const colorClasses = {
      blue: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
      gray: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600',
      red: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
      indigo: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
      green: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
      teal: 'bg-teal-100 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800',
      purple: 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
      emerald: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
      orange: 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800'
    };
    
    // Find matching role or create badge from the text itself
    const badges = keysArray.map((key, index) => {
      const role = roleOptions.find(r => 
        r.key === key || 
        r.label?.toLowerCase() === key?.toLowerCase()
      );
      console.log(`  Key: ${key}, Found role:`, role?.label);
      
      // If no match, create a simple badge from the text
      if (!role) {
        return (
          <span key={index} className={`inline-block px-2 py-1 text-xs rounded-full border ${colorClasses.gray} mr-1 mb-1 font-medium`}>
            {key}
          </span>
        );
      }
      
      const colorClass = colorClasses[role.color] || colorClasses.blue;
      
      return (
        <span key={role.key} className={`inline-block px-2 py-1 text-xs rounded-full border ${colorClass} mr-1 mb-1 font-medium`}>
          {role.label}
        </span>
      );
    });
    
    return badges;
  };

  // Get current list based on view
  const currentUserList = userListView === 'employees' ? users : suppliers;
  
  const filteredUsers = currentUserList.filter(u => {
    const name = typeof u.name === 'string' ? u.name : u.name?.full || '';
    const email = u.email || '';
    const searchLower = searchTerm.toLowerCase();
    return name.toLowerCase().includes(searchLower) || email.toLowerCase().includes(searchLower);
  });

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">Access denied. Admin privileges required.</p>
        </div>
      </div>
    );
  }

  if (loading && activeTab === 'smtp') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Settings className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <Settings className="w-7 h-7 text-blue-600 dark:text-blue-400" />
          Global Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400">Configure system-wide settings and manage users</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-medium transition-colors relative ${
              activeTab === 'users'
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              User Management
            </div>
            {activeTab === 'users' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"></div>
            )}
          </button>

          <button
            onClick={() => setActiveTab('knack')}
            className={`px-4 py-2 font-medium transition-colors relative ${
              activeTab === 'knack'
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Knack API
            </div>
            {activeTab === 'knack' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"></div>
            )}
          </button>

          <button
            onClick={() => setActiveTab('smtp')}
            className={`px-4 py-2 font-medium transition-colors relative ${
              activeTab === 'smtp'
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              SMTP Settings
            </div>
            {activeTab === 'smtp' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"></div>
            )}
          </button>

          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2 font-medium transition-colors relative ${
              activeTab === 'dashboard'
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <Monitor className="w-5 h-5" />
              Central Dashboard
            </div>
            {activeTab === 'dashboard' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"></div>
            )}
          </button>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          )}
          <p className={`text-sm ${
            message.type === 'success'
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          }`}>
            {message.text}
          </p>
        </div>
      )}

      {/* Knack API Tab */}
      {activeTab === 'knack' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-2 mb-6">
            <Settings className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Knack API Configuration</h2>
          </div>

          <form onSubmit={handleSaveKnackSettings} className="space-y-6">
            {/* Knack App ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Knack Application ID *
              </label>
              <input
                type="text"
                value={knackSettings.knack_app_id}
                onChange={(e) => setKnackSettings({ ...knackSettings, knack_app_id: e.target.value })}
                placeholder="623c9ff308fe1d001e8ecefe"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                required
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Found in Knack Settings → API & Code
              </p>
            </div>

            {/* Knack API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Knack API Key *
              </label>
              <input
                type="text"
                value={knackSettings.knack_api_key}
                onChange={(e) => setKnackSettings({ ...knackSettings, knack_api_key: e.target.value })}
                placeholder="API key from Knack"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                required
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                API Key with full access permissions
              </p>
            </div>

            {/* Knack API URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Knack API Base URL
              </label>
              <input
                type="text"
                value={knackSettings.knack_api_url}
                onChange={(e) => setKnackSettings({ ...knackSettings, knack_api_url: e.target.value })}
                placeholder="https://api.knack.com/v1"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Usually https://api.knack.com/v1 (EU region: https://api.knack.eu/v1)
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-600 dark:text-blue-400">
                <strong>Note:</strong> These credentials are used to connect to your Knack database. 
                After updating, restart the backend service for changes to take effect.
              </p>
            </div>

            {/* Current Configuration Display */}
            <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Currently Active Configuration:</h3>
              <div className="space-y-1 text-xs font-mono text-gray-600 dark:text-gray-400">
                <div><span className="font-semibold">App ID:</span> {knackSettings.knack_app_id || 'Not configured'}</div>
                <div><span className="font-semibold">API Key:</span> {knackSettings.knack_api_key ? '••••••••' + knackSettings.knack_api_key.slice(-8) : 'Not configured'}</div>
                <div><span className="font-semibold">API URL:</span> {knackSettings.knack_api_url}</div>
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                These are the credentials currently being used by the backend service.
              </p>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Save Knack Settings
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* SMTP Settings Tab */}
      {activeTab === 'smtp' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-2 mb-6">
            <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">SMTP Email Configuration</h2>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            {/* SMTP Host */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                SMTP Host *
              </label>
              <input
                type="text"
                value={settings.smtp_host}
                onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                placeholder="smtp.gmail.com"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* SMTP Port */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                SMTP Port *
              </label>
              <input
                type="number"
                value={settings.smtp_port}
                onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
                placeholder="587"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Common ports: 587 (TLS), 465 (SSL), 25 (unencrypted)
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* SMTP Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  SMTP Username *
                </label>
                <input
                  type="text"
                  value={settings.smtp_username}
                  onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                  placeholder="your-email@example.com"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* SMTP Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  SMTP Password *
                </label>
                <input
                  type="password"
                  value={settings.smtp_password}
                  onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* From Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  From Email *
                </label>
                <input
                  type="email"
                  value={settings.smtp_from_email}
                  onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
                  placeholder="noreply@example.com"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* From Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  From Name
                </label>
                <input
                  type="text"
                  value={settings.smtp_from_name}
                  onChange={(e) => setSettings({ ...settings, smtp_from_name: e.target.value })}
                  placeholder="QC System"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-600 dark:text-blue-400">
                <strong>Note:</strong> These SMTP settings will be used to send email notifications to suppliers 
                when QC incidents are reported. Make sure to test your configuration after saving.
              </p>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* User Management Tab */}
      {activeTab === 'users' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          {/* Header with Instructions */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">User Management</h2>
              </div>
              
              {/* Employee/Supplier Toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setUserListView('employees')}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    userListView === 'employees'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Employees ({users.length})
                </button>
                <button
                  onClick={() => setUserListView('suppliers')}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    userListView === 'suppliers'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Suppliers ({suppliers.length})
                </button>
              </div>
            </div>
          </div>

          {/* Search and Create Button */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex gap-3">
              <input
                type="text"
                placeholder={`Search ${userListView === 'employees' ? 'employees' : 'suppliers'} by name or email...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => handleOpenUserModal(null)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 font-medium"
              >
                <UserPlus className="w-5 h-5" />
                Create User
              </button>
            </div>
          </div>

          {/* Users List */}
          <div className="p-6">
            {usersLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">Loading users...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-gray-500 dark:text-gray-400">No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Department</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Roles</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => {
                      // Get avatar URL
                      const avatarUrl = u.field_115_raw?.s3?.url || u.field_115_raw?.url || u.field_115;
                      const userName = u.field_12 || 'Unknown';
                      const userEmail = u.field_13_raw?.email || 'No email';
                      
                      return (
                        <tr key={u.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              {avatarUrl ? (
                                <img 
                                  src={avatarUrl} 
                                  alt={userName}
                                  className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div 
                                className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium border-2 border-gray-200 dark:border-gray-600"
                                style={{ display: avatarUrl ? 'none' : 'flex' }}
                              >
                                {userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                              </div>
                              <span className="text-gray-900 dark:text-white font-medium">{userName}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{userEmail}</td>
                          <td className="py-3 px-4">
                            {u.department ? (
                              <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded text-xs font-medium">
                                {u.department}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">No Dept</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1">
                              {getRoleBadges(u.profile_keys)}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleOpenUserModal(u)}
                                className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                title="Edit user"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteUser(u.id)}
                                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                title="Delete user"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingUser ? 'Edit User' : 'User Management Info'}
              </h3>
              <button
                onClick={handleCloseUserModal}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSaveUser} className="p-6 space-y-6" onPaste={handleAvatarPaste}>
              {/* Avatar Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Profile Image
                </label>
                <div className="flex items-center gap-4">
                  {userFormData.avatarPreview ? (
                    <img 
                      src={userFormData.avatarPreview} 
                      alt="Avatar preview"
                      className="w-20 h-20 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium border-2 border-gray-200 dark:border-gray-600 text-xl">
                      {userFormData.name ? userFormData.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?'}
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept="image/*"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
                      disabled={uploadingAvatar}
                    >
                      <Upload className="w-4 h-4" />
                      {uploadingAvatar ? 'Uploading...' : 'Upload Image'}
                    </button>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Or paste an image from clipboard
                    </p>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={userFormData.name}
                  onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                  placeholder="Enter full name"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  placeholder="user@example.com"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={userFormData.phone}
                  onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Password - Optional */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Password (Optional)
                </label>
                <input
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                  placeholder="Leave blank to keep current password"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Only fill this if you want to change the user's password
                </p>
              </div>

              {/* Department */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Department
                </label>
                <select
                  value={userFormData.department || ''}
                  onChange={(e) => setUserFormData({ ...userFormData, department: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No Department</option>
                  <option value="Airports">Airports</option>
                  <option value="BD">BD</option>
                  <option value="Finance">Finance</option>
                  <option value="HQ">HQ</option>
                  <option value="PMO">PMO</option>
                </select>
              </div>

              {/* Roles */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  User Roles *
                </label>
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  {roleOptions.map((role) => (
                    <label
                      key={role.key}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={userFormData.profile_keys.includes(role.key)}
                        onChange={() => handleToggleRole(role.key)}
                        className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">{role.label}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{role.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSendCredentials(editingUser?.id);
                  }}
                  disabled={sendingCredentials || !editingUser}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2"
                >
                  {sendingCredentials ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Credentials
                    </>
                  )}
                </button>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCloseUserModal}
                    className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Central Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <DashboardSettings />
      )}
    </div>
  );
};

export default GlobalSettings;
